// resources/js/admin/hooks/cms/usePageMedia.ts

import { useState, useEffect, useCallback } from "react";
import useToken from "../auth/useToken";
import { PageMedia, PageMediaAttachData, PageMediaSyncData } from "../../../types";
import { pageMediaService } from "../../../services/pageMediaService";
import { PaginatedResponse } from "../../../types/core/PaginatedResponse";

export default function usePageMedia(pageId: string) {
  const { token } = useToken();
  const [media, setMedia] = useState<PageMedia[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔒 PROTÉGÉ - Token requis (AVEC PAGINATION)
  const fetchMedia = useCallback(async (page: number = 1, perPage: number = 20) => {
    if (!token) {
      setError("Vous devez être connecté");
      return;
    }

    if (!pageId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await pageMediaService.getByPage(token, pageId, page, perPage);
      setMedia(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des médias de la page");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, pageId]);

  // 🔒 PROTÉGÉ - Token requis (AVEC PAGINATION)
  const fetchMediaByField = useCallback(async (field: string, page: number = 1, perPage: number = 20): Promise<PaginatedResponse<PageMedia> | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    if (!pageId) return null;

    setLoading(true);
    setError(null);
    try {
      const response = await pageMediaService.getByField(token, pageId, field, page, perPage);
      return response;
    } catch (err) {
      setError(`Erreur lors du chargement des médias pour le champ ${field}`);
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, pageId]);

  // 🔒 PROTÉGÉ - Token requis
  const attachMedia = useCallback(async (data: PageMediaAttachData): Promise<PageMedia | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    if (!pageId) {
      setError("Page non spécifiée");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newMedia = await pageMediaService.attach(token, pageId, data);
      setMedia((prev) => [...prev, newMedia]);
      return newMedia;
    } catch (err) {
      setError("Erreur lors de l'attachement du média");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, pageId]);

  // 🔒 PROTÉGÉ - Token requis
  const detachMedia = useCallback(async (mediaId: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    if (!pageId) {
      setError("Page non spécifiée");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await pageMediaService.detach(token, pageId, mediaId);
      setMedia((prev) => prev.filter((m) => m.media_id !== mediaId));
      return true;
    } catch (err) {
      setError("Erreur lors du détachement du média");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token, pageId]);

  // 🔒 PROTÉGÉ - Token requis
  const syncMedia = useCallback(async (data: PageMediaSyncData): Promise<PageMedia[] | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    if (!pageId) {
      setError("Page non spécifiée");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newMedia = await pageMediaService.sync(token, pageId, data);
      setMedia((prev) => {
        // Supprimer les anciens médias du champ
        const filtered = prev.filter((m) => m.field !== data.field);
        return [...filtered, ...newMedia];
      });
      return newMedia;
    } catch (err) {
      setError("Erreur lors de la synchronisation des médias");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, pageId]);

  // 🔒 PROTÉGÉ - Token requis
  const updateMediaPosition = useCallback(async (mediaId: string, position: number, field?: string): Promise<PageMedia | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    if (!pageId) {
      setError("Page non spécifiée");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updated = await pageMediaService.updatePosition(token, pageId, mediaId, position, field);
      setMedia((prev) => prev.map((m) => (m.media_id === mediaId ? updated : m)));
      return updated;
    } catch (err) {
      setError("Erreur lors de la mise à jour de la position");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, pageId]);

  // 🔓 PUBLIC - Grouper les médias par champ
  const getGroupedByField = useCallback((): Record<string, PageMedia[]> => {
    return media.reduce((acc, item) => {
      if (!acc[item.field]) {
        acc[item.field] = [];
      }
      acc[item.field].push(item);
      return acc;
    }, {} as Record<string, PageMedia[]>);
  }, [media]);

  useEffect(() => {
    if (token && pageId) {
      fetchMedia();
    }
  }, [token, pageId, fetchMedia]);

  return {
    media,
    loading,
    error,
    pagination,
    fetchMedia,
    fetchMediaByField,
    attachMedia,
    detachMedia,
    syncMedia,
    updateMediaPosition,
    getGroupedByField,
  };
}